import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';

import type { ExportInfo } from '../../api/uploadedFiles';
import type { ColumnSelectionItem } from '../../components/templates/ColumnSelection';

/** The slice of `ColumnDef` the export cares about. Structurally satisfied by
 *  every list table's `ColumnDef` (ServiceTable, StructureTable, UserTable,
 *  PartTable, MovementTable, ShipmentTable), so pages pass theirs directly. */
export interface ExportColumn {
  key: string;
  label: string;
  visible: boolean;
}

/** A filter model shaped like MUI's, but tolerant of the looser
 *  `{ items?: [...] }` literals some pages hold in state. */
export type ExportFilterModel = GridFilterModel | { items?: GridFilterItem[] };

export interface BuildExportPayloadInput {
  fileType: 'csv' | 'xlsx';
  /** `ListType` value the BFF keys its column projection off. */
  fileName: string;
  /** Full column list in on-screen order. Hidden entries are dropped, and the
   *  surviving order is what the exported file's columns are ordered by. */
  columns: ExportColumn[];
  /** The column-picker tree. The BFF projects row *data* from this, while it
   *  renders *headers* from `selectedColumns` — a field present in one but not
   *  the other is the classic "column exports blank" bug. */
  columnSelectionData?: ColumnSelectionItem[];
  /** Per-page fix-up applied to `columnSelectionData` on the wire — e.g.
   *  ServiceList's `prepareColumnSelectionForBFF`, which remaps the UI's "Job"
   *  section title to the "Service" title api-services projects by. */
  transformColumnSelection?: (items: ColumnSelectionItem[]) => unknown;
  /** Omit (or pass `undefined`) for lists filtered entirely client-side; the
   *  payload then carries `{ items: [] }` so the BFF exports the whole list. */
  filterModel?: ExportFilterModel;
  /** Mirrors the wire transform the page's *list* query applies to its filter
   *  items, so an export matches what is on screen instead of erroring on the
   *  BFF (`mapNumericFilterItemsForWire`, `mapServiceFilterOperatorForWire`,
   *  `mapPartFilterOperatorForWire`). */
  mapFilterItems?: (items: GridFilterItem[]) => GridFilterItem[];
  rowCount?: number;
  pageSize?: number;
  sorting?: Array<Record<string, unknown>>;
  /** Half-open UTC instants (ISO-8601 `Z`) for date-windowed lists. */
  startDate?: string;
  endDate?: string;
  /** Free-text multi-field search. Blank/whitespace-only is omitted entirely
   *  rather than sent as `''`, which the BFF treats as a real search term. */
  multiSearchQuery?: string;
  /** Page-specific extras — `structureTypeCategory`, `locationId`, … */
  extra?: Partial<ExportInfo>;
}

/** Drops keys whose value is `undefined` so a page that omits an optional
 *  field produces the exact same wire body as one that never mentioned it. */
const stripUndefined = <T extends object>(payload: T): T =>
  Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined)) as T;

/**
 * Builds the `POST /export` payload from a list page's on-screen state.
 *
 * This is the single place the export payload is shaped. It exists because the
 * same ~35 lines were hand-copied into ten list pages and drifted, producing
 * exports whose columns, filters, or search did not match the screen.
 *
 * The two column arrays are derived from one filtered list, so they can never
 * disagree in membership or order.
 */
export const buildExportPayload = ({
  fileType,
  fileName,
  columns,
  columnSelectionData,
  transformColumnSelection,
  filterModel,
  mapFilterItems,
  rowCount,
  pageSize,
  sorting,
  startDate,
  endDate,
  multiSearchQuery,
  extra,
}: BuildExportPayloadInput): ExportInfo => {
  const visibleColumns = columns.filter((c) => c.visible);
  const items = filterModel?.items ?? [];

  const payload: ExportInfo = {
    fileType,
    fileName,
    filter: filterModel
      ? { ...filterModel, items: mapFilterItems ? mapFilterItems(items) : items }
      : { items: [] },
    selectedColumns: visibleColumns.map((c) => ({ label: c.label, value: c.key })),
    visibleColumnsField: visibleColumns.map((c) => c.key),
    columnSelectionData:
      columnSelectionData && transformColumnSelection
        ? transformColumnSelection(columnSelectionData)
        : columnSelectionData,
    rowCount,
    pageSize,
    sorting,
    startDate,
    endDate,
    multiSearchQuery: multiSearchQuery?.trim() || undefined,
    ...extra,
  };

  return stripUndefined(payload);
};

/** Fields ticked in a column-selection tree, whatever shape the per-page
 *  transform left it in. Returns `null` when the tree is absent or does not
 *  match the `{ title, value: [{ field, value }] }` shape. */
export const tickedExportFields = (columnSelectionData: unknown): Set<string> | null => {
  if (!Array.isArray(columnSelectionData)) return null;
  const ticked = new Set<string>();
  for (const group of columnSelectionData) {
    const groupValue = (group as ColumnSelectionItem)?.value;
    if (!Array.isArray(groupValue)) return null;
    for (const col of groupValue) {
      if (col?.value) ticked.add(col.field);
    }
  }
  return ticked;
};

/**
 * Visible column keys that the BFF has not been asked to project — i.e. the
 * columns that would export with a header but no data. Empty when the payload
 * carries no column-selection tree (those lists project every field).
 */
export const findUnprojectedExportFields = (payload: ExportInfo): string[] => {
  const ticked = tickedExportFields(payload.columnSelectionData);
  if (!ticked) return [];
  return (payload.visibleColumnsField ?? []).filter((field) => !ticked.has(field));
};
